import {objectFromEntries, pick} from '@benchling/js-utils';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {setWindowTitle} from 'BenchlingUtil';
import BufferOptions from 'buffers/BufferOptions';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readFragmentFromCache, writeFragmentToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {clearNavigationWarnings} from 'lib/navigateToURL';
import {StructuredTableInsertionContext} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {getMaterialFlows} from 'procedures/design/flowchartManagementUtils';
import LegacyProcedureEditor from 'procedures/design/LegacyProcedureEditor';
import LegacyProcedureEditorHeader from 'procedures/design/LegacyProcedureEditorHeader';
import {DEFAULT_METHOD_EDITOR_TAB} from 'procedures/design/method-config/MethodConfigConstants';
import useValidateProcedure from 'procedures/design/method-config/useValidateProcedure';
import {HistoryManagerContext} from 'procedures/design/ProcedureHistoryPanel';
import {getProcedureIcon} from 'procedures/design/procedureIconUtils';
import useEditingMethodNameState from 'procedures/design/useEditingMethodNameState';
import useLegacyPreSaveImmediatelyMiddleware from 'procedures/design/useLegacyPreSaveImmediatelyMiddleware';
import PromiseFunctionQueue from 'procedures/design/utils/PromiseFunctionQueue';
import determineProcedureAccessLevel, {
  ProcedureAccessLevel,
} from 'procedures/design/validation/determineProcedureAccessLevel';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import {ProcedurePageThinQuery_procedure_ProcedureNode as ThinNetworkProcedure} from 'procedures/graphql/__generated/ProcedurePageThinQuery';
import parseProcedureFromNetwork from 'procedures/network/parseProcedureFromNetwork';
import * as ProcedureDAO from 'procedures/network/ProcedureDAO';
import serializeProcedureFlowchartForNetwork from 'procedures/network/serializeProcedureFlowchartForNetwork';
import {
  MaterialFlow,
  ProcedureFlowchart,
  ProcedureMetadata,
  ProcedureWithMetadata,
} from 'procedures/ProcedureDomainTypes';
import {normalizeFlowchartOrderingForComparison} from 'procedures/procedurePagePersistenceUtils';
import {ProcedureProcessesPlugin} from 'procedures/procedures-platform/ProcedureProcessesPlugin';
import {duplicateLegacyProcedure, duplicateProcedure} from 'procedures/utils/duplicateProcedure';
import flushDocumentStore from 'procedures/utils/flushDocumentStore';
import {getPowAgnosticPublishedFlowchartVersionId} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {useAllTagSchemasWithStandardShape} from 'procedures/utils/useAllTagSchemas';
import ProcessFlowchartConfigEditor from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditor';
import {PublishingRecordForVersionHistoryPanel} from 'template-collectibles/publishing-ui/graphql/types';
import getTestProps from 'test/lib/cypress/getTestProps';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import memoizeLastCall from 'util/memoizeLastCall';

import useLegacyProcedureSaveTools from './design/useLegacyProcedureSaveTools';
import useProcedureActions, {useLegacyProcedureActions} from './design/useProcedureActions';
import useProcedureReadonlyState from './design/useProcedureReadonlyState';
import {IdToCidMap, invertStringMapping} from './network/procedureNetworkUtils';
import {
  GetCachedCidFunctionFactory,
  INACCESSIBLE_FLOWCHART_VERSION_ID,
  NetworkProcedure,
  LegacyProcedureAutosaveManager,
  ProcedureCapabilityChecker,
  ProcedureFlowchartStateManager,
  ProcedureLegacyFlowchartVersionLoader,
  ProcedureHardRefreshManager,
  ProcedureLoader,
  ProcedureVersionHistoryManager,
  useProcedurePageIdToCidMap,
  ProcedureVersionInfoMerger,
} from './ProcedurePageComponents';
import {PersistenceInfo, ProcedurePageContext, ProcedurePageContextValue} from './ProcedurePageContext';

interface ProcedurePageProps {
  procedureId: string;
  flowchartVersionId?: string;
}

function getLatestVersionId(
  thinProcedureData: ThinNetworkProcedure,
  getLegacyVersionIdEvenIfInaccessible?: boolean
): string {
  if (
    thinProcedureData.processFlowchartConfig != null &&
    BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION
  ) {
    const fccv = thinProcedureData.processFlowchartConfig.currentFlowchartConfigVersion;
    return fccv && !isInaccessible(fccv) ? fccv.id : INACCESSIBLE_FLOWCHART_VERSION_ID;
  }

  const {latestFlowchartVersion} = thinProcedureData.procedureFlowchart;
  return !isInaccessible(latestFlowchartVersion)
    ? latestFlowchartVersion.id
    : getLegacyVersionIdEvenIfInaccessible
      ? latestFlowchartVersion.invalidId
      : INACCESSIBLE_FLOWCHART_VERSION_ID;
}

export default function ProcedurePage({
  procedureId,
  flowchartVersionId,
}: ProcedurePageProps): JSX.Element | null {
  /**
   * The {key} props in the components below are specific and intentional, to ensure that
   * we keep existing component instances whenever we can.
   */
  return (
    <ProcedureHardRefreshManager>
      {(hardRefresh, refreshKey) => (
        // First: load the procedure (without the flowchart)
        <ProcedureLoader procedureId={procedureId} key={refreshKey}>
          {(thinProcedureData, refetchProcedure) => (
            // Next: the version history mgr tracks old versions, which one is visible, manages the URL
            <LegacyProcedureAutosaveManager procedure={thinProcedureData}>
              <ProcedureVersionHistoryManager
                procedureId={procedureId}
                flowchartConfigId={thinProcedureData.processFlowchartConfig?.id}
                latestVersionId={getLatestVersionId(thinProcedureData)}
                flowchartVersionId={flowchartVersionId}
                publishedFlowchartVersionId={
                  getPowAgnosticPublishedFlowchartVersionId(thinProcedureData) ?? undefined
                }
                publishingRecords={accessibleObjs(thinProcedureData.publishingRecords)}
              >
                {(versionIdToView, setVersionIdToView) => (
                  // Next: ensure we have sufficient capabilities to view the procedure
                  <ProcedureCapabilityChecker procedureData={thinProcedureData}>
                    {/* Next: load the requested flowchart version (if not provided, or if it's PoW: the latest version) */}
                    <ProcedureLegacyFlowchartVersionLoader
                      procedureData={thinProcedureData}
                      procedureFlowchartVersionId={versionIdToView}
                      key={versionIdToView}
                    >
                      {(
                        fullProcedureData,
                        refetchLegacyFlowchartVersion,
                        loadedFlowchartVersion,
                        isViewingOldVersion
                      ) => (
                        <ProcedureVersionInfoMerger
                          fullProcedureData={fullProcedureData}
                          loadedLegacyFlowchartVersion={loadedFlowchartVersion}
                          isViewingOldLegacyVersion={isViewingOldVersion}
                        >
                          {(versionMetadata) => (
                            // Finally, load the write manager, which also renders the procedure page
                            <ProcedurePageWriteManager
                              procedureGraphQL={fullProcedureData}
                              versionMetadata={versionMetadata}
                              refetchLegacyProcedure={() => refetchLegacyFlowchartVersion(refetchProcedure)}
                              hardRefresh={hardRefresh}
                              onSwitchVersionId={setVersionIdToView}
                            />
                          )}
                        </ProcedureVersionInfoMerger>
                      )}
                    </ProcedureLegacyFlowchartVersionLoader>
                  </ProcedureCapabilityChecker>
                )}
              </ProcedureVersionHistoryManager>
            </LegacyProcedureAutosaveManager>
          )}
        </ProcedureLoader>
      )}
    </ProcedureHardRefreshManager>
  );
}

/**
 * This function performs a "clean" save: it doesn't push any values into state or redux. It just calls the API.
 */
async function saveLegacyProcedure(
  procedure: ProcedureWithMetadata,
  persistenceInfo?: PersistenceInfo,
  lastSavedFlowchartVersionId?: string
): Promise<Record<string, string>> {
  const {
    metadata: {name, id, templateCollection},
  } = procedure;

  let updatedId: string = '';
  let cidToIdMap: Record<string, string> = {};

  const result = await ProcedureDAO.updateLegacyProcedure({
    id,
    name,
    flowchart: serializeProcedureFlowchartForNetwork(
      procedure.flowchart,
      procedure.metadata.procedureType,
      persistenceInfo,
      lastSavedFlowchartVersionId
    ),
    templateCollectionId: templateCollection.id,
  });

  updatedId = result.updatedIds[0];
  cidToIdMap = result.cidToIdMap;

  assert(id === updatedId, 'updated procedure id does not match provided id');
  return cidToIdMap;
}

/**
 * When we unmount, we need to curry in the lastSavedFlowchartVersionId, to ensure it's provided correctly
 */
function saveLegacyProcedureWhenUnmountedFactory(
  lastSavedFlowchartVersionId: string
): (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => Promise<null> {
  let saved = false;

  return async (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => {
    if (saved) {
      // Ensure that we don't save the procedure multiple times after it's been unmounted
      return null;
    }
    await saveLegacyProcedure(procedure, persistenceInfo, lastSavedFlowchartVersionId);
    saved = true;

    // Clean up the document store, to ensure we don't have any stale IDs. Without this cleanup,
    // it's fairly easy to end up with a server error (mismatched version) the next time we autosave.
    flushDocumentStore(procedure);
    return null;
  };
}

type UpdateLegacyProcedure = (
  procedure: ProcedureWithMetadata,
  persistenceInfo?: PersistenceInfo
) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>;

const memoizedParseProcedureFromNetwork = memoizeLastCall(
  (procedureGraphQL: NetworkProcedure, getCachedCidFactory: GetCachedCidFunctionFactory) =>
    parseProcedureFromNetwork(procedureGraphQL, getCachedCidFactory()),
  {useDeepEquality: true}
);

const memoizedGetCachedCidFactory = memoizeLastCall(
  (map: IdToCidMap, getCachedCidFactory: GetCachedCidFunctionFactory) => () => getCachedCidFactory(map),
  {
    useDeepEquality: true,
  }
);

/** @package Exported for testing
 *
 * For PoW procedures, the legacy flowchart data passed through here is not used
 * for rendering, ProcessFlowchartConfigEditor handles it. When the legacy
 * version is inaccessible (e.g. read-only users viewing a draft version),
 * ProcedureLegacyFlowchartVersionLoader returns a dummy NetworkProcedure, and
 * parseProcedureFromNetwork returns an empty flowchart ({nodes: [], edges:
 * []}).
 */
export function ProcedurePageWriteManager(props: {
  procedureGraphQL: NetworkProcedure;
  versionMetadata: {id: string; createdAt: string; isLatest: boolean};
  onSwitchVersionId: (versionId: string) => void;
  refetchLegacyProcedure: () => Promise<NetworkProcedure | null> | null;
  hardRefresh: () => void;
}): JSX.Element {
  const isMountedRef = useIsNotUnmounted();

  // The last known flowchart version (or flowchart config version) per-client that we will be saving updates on top of.
  // This will be used by the server to determine if the session is using a stale version of the procedure
  // to prevent concurrent edits from overwriting each other
  const lastSavedFlowchartVersionId = useRef<string>(getLatestVersionId(props.procedureGraphQL, true));
  const [legacyFullyBlockedOnServerError, setLegacyFullyBlockedOnServerError] = useStateIfMounted(false);
  const {setIdToCidMap, getCachedCidFactory} = useProcedurePageIdToCidMap(props.procedureGraphQL);
  const currentProcedure = useMemo(
    () => parseProcedureFromNetwork(props.procedureGraphQL, getCachedCidFactory()),
    [props.procedureGraphQL, getCachedCidFactory]
  );
  const {refetchLegacyProcedure} = props;
  const powMetadataSaveQueue = useRef(new PromiseFunctionQueue());

  const updatePoWProcedureMetadata = (metadata: ProcedureMetadata): void => {
    powMetadataSaveQueue.current.enqueue(async () => {
      await ProcedureDAO.updateProcedureMetadata({
        ...pick(metadata, 'id', 'name'),
        templateCollectionId: metadata.templateCollection.id,
      });
    });
  };

  useOnMount(() => () => {
    // Clear the metadata-save queue on unmount. Any in-flight save will still run.
    powMetadataSaveQueue.current.kill();
  });

  const updateLegacyProcedure: UpdateLegacyProcedure = useCallback(
    async (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => {
      let cidToIdMap;
      try {
        cidToIdMap = await saveLegacyProcedure(
          procedure,
          persistenceInfo,
          lastSavedFlowchartVersionId.current
        );
      } catch (e) {
        if ([409, 500].includes(e.status)) {
          // Any error that should force the procedure into a read-only state that is recovered on refresh
          // should be handled here. Currently, this handles 409 state conflicts and general 500 errors.
          setLegacyFullyBlockedOnServerError(true);
        }
        throw e;
      }

      const newIdToCidMap = invertStringMapping(cidToIdMap);

      // The next render cycle triggered by this setState call will NOT see the most up-to-date refetched ids
      setIdToCidMap((cachedIdToCidMap) => ({...cachedIdToCidMap, ...newIdToCidMap}));
      // The next render cycle triggered by this refetch call will see the most up-to-date refetched ids
      const updatedNetworkProcedure = await refetchLegacyProcedure();
      // The next render cycle triggered by this setState call will see the most up-to-date refetched ids
      setIdToCidMap(newIdToCidMap);

      if (isMountedRef.current) {
        // The call to `refetch` may return null. This happens if the component has been unmounted in the interim.
        // In that case, the return value here isn't important.
        let updatedProcedure = procedure;
        if (updatedNetworkProcedure != null) {
          assert(
            !isInaccessible(updatedNetworkProcedure.procedureFlowchart.latestFlowchartVersion),
            'Expected latest flowchart version to be accessible after write'
          );
          lastSavedFlowchartVersionId.current =
            updatedNetworkProcedure.procedureFlowchart.latestFlowchartVersion.id;
          updatedProcedure = memoizedParseProcedureFromNetwork(
            updatedNetworkProcedure,
            memoizedGetCachedCidFactory(newIdToCidMap, getCachedCidFactory)
          );
        }

        return {
          updatedProcedure,
          cidToIdMap,
        };
      } else {
        return null;
      }
    },
    [
      setIdToCidMap,
      getCachedCidFactory,
      refetchLegacyProcedure,
      isMountedRef,
      setLegacyFullyBlockedOnServerError,
    ]
  );

  return (
    <ProcedureFlowchartStateManager
      procedureId={props.procedureGraphQL.id}
      legacyPersistedFlowchart={currentProcedure.flowchart}
      procedureMetadata={currentProcedure.metadata}
    >
      {(
        legacyPersistedFlowchart,
        legacyDraftFlowchart,
        legacyDraftFlowchartRef,
        setLegacyDraftFlowchart,
        procedureMetadata,
        setProcedureMetadata,
        {
          legacyDirtyState,
          readonlyState,
          legacyIsSaveInFlightState,
          legacyIsAutosaverPausedState,
          legacyIsAutosaverBlockedState,
        }
      ) => (
        <PresentationalProcedurePage
          procedureMetadata={procedureMetadata}
          processFlowchartConfigId={currentProcedure.processFlowchartConfigId}
          legacyPersistedFlowchart={legacyPersistedFlowchart}
          legacyDraftFlowchart={legacyDraftFlowchart}
          legacyDraftFlowchartRef={legacyDraftFlowchartRef}
          setLegacyDraftFlowchart={setLegacyDraftFlowchart}
          setProcedureMetadata={(metadata) => {
            // This sets the metadata in redux, but doesn't setDirty or trigger a save
            setProcedureMetadata(metadata);

            // In PoW, we send the metadata to the server immediately, as the normal autosave
            // mechanism only saves the flowchart data.
            if (
              currentProcedure.processFlowchartConfigId != null &&
              BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION
            ) {
              // Update the Apollo cache so ProcedureFileState picks up the new
              // name for the workspace tab.
              const procedureFragment = getFragmentWrapper('Procedure');
              const cached = readFragmentFromCache(procedureFragment, metadata.id);
              if (cached != null) {
                writeFragmentToCache(procedureFragment, {...cached, name: metadata.name});
              }

              updatePoWProcedureMetadata(metadata);
            }
          }}
          legacyDirtyState={legacyDirtyState}
          readonlyState={readonlyState}
          legacyIsSaveInFlightState={legacyIsSaveInFlightState}
          legacyIsAutosaverPausedState={legacyIsAutosaverPausedState}
          legacyIsAutosaverBlockedState={legacyIsAutosaverBlockedState}
          duplicateProcedure={(params) =>
            currentProcedure.processFlowchartConfigId == null
              ? duplicateLegacyProcedure({...params, currentProcedure})
              : duplicateProcedure({
                  name: params.name,
                  templateCollectionId: params.templateCollectionId,
                  procedureType: params.procedureType,
                  sourceProcedureId: currentProcedure.metadata.id,
                })
          }
          updateAndPersistLegacyProcedure={updateLegacyProcedure}
          legacyFullyBlockedOnServerError={legacyFullyBlockedOnServerError}
          onSwitchVersionId={props.onSwitchVersionId}
          publishingRecords={accessibleObjs(props.procedureGraphQL.publishingRecords)}
          currentFlowchartVersionMetadata={props.versionMetadata}
          lastSavedFlowchartVersionId={lastSavedFlowchartVersionId}
          hardRefresh={props.hardRefresh}
          key={currentProcedure.metadata.id}
        />
      )}
    </ProcedureFlowchartStateManager>
  );
}

interface PresentationalProcedurePageProps {
  procedureMetadata: ProcedureMetadata;
  processFlowchartConfigId: string | null;
  legacyPersistedFlowchart: ProcedureFlowchart;
  legacyDraftFlowchart: ProcedureFlowchart;
  legacyDraftFlowchartRef: React.MutableRefObject<ProcedureFlowchart>;
  setLegacyDraftFlowchart: (fc: ProcedureFlowchart) => void;
  setProcedureMetadata: (procedureMetadata: ProcedureMetadata) => void;
  legacyDirtyState: [boolean, (isDirty: boolean) => void];
  readonlyState: [boolean, (isReadonly: boolean) => void];
  legacyIsSaveInFlightState: [boolean, (isSaveInFlight: boolean) => void];
  legacyIsAutosaverPausedState: [boolean, (isPaused: boolean) => void];
  legacyIsAutosaverBlockedState: [boolean, (isBlocked: boolean) => void];
  updateAndPersistLegacyProcedure: (
    procedure: ProcedureWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>;
  duplicateProcedure: (params: {
    name: string;
    templateCollectionId: string;
    procedureType: ProcedureType;
  }) => Promise<void>;
  // If the WriteManager determines we should block the user from making changes due to an error.
  // This will make the procedure read-only in its unsaved state and force the user to refresh
  legacyFullyBlockedOnServerError: boolean;
  currentFlowchartVersionMetadata: {id: string; createdAt: string; isLatest: boolean};
  lastSavedFlowchartVersionId: React.MutableRefObject<string>;
  publishingRecords: Array<PublishingRecordForVersionHistoryPanel>;
  hardRefresh: () => void;
  onSwitchVersionId: (versionId: string) => void;
}

/**
 * All about draft state management:
 *
 * We keep three (!) copies of the flowchart in memory. This explains why, and what they are each used for.
 *
 * [1] REDUX
 *
 * This is the source of truth for the flowchart. The other two are just for convenience and/or temporal correctness,
 * as described below. In this component, the Redux store is accessed via the props `legacyDraftFlowchart` and `setLegacyDraftFlowchart`.
 * These props are provided by ProcedureFlowchartStateManager, which mediates between the UI and Redux.
 *
 * [2] REF
 *
 * The `legacyDraftFlowchartRef` prop is also managed by ProcedureFlowchartStateManager. Any changes to the flowchart are
 * immediately saved in the ref, at the same time that they are sent to Redux.
 *
 * The value of using the ref is that it is immune to closure staleness. Even if the ref is stale, the contents of
 * the flowchart are stored one level deeper (ref.current), so changes made to it are available immediately and reliably.
 * This has been necessary to avoid stale flowchart data in `handleSave`, which does a lot of async juggling.
 *
 * [3] LOCAL COMPONENT STATE
 *
 * Finally, the component below has its own local state variable for the draft flowchart. This is needed because
 * we've built out a lot of `setState` calls using the modifier callback form: `setLegacyDraftFlowchart(fc => fc)`.
 * Unfortunately, we can't do this with Redux, which requires a hard setter: `setLegacyDraftFlowchart(fc)`.
 * In order to keep the modifier callbacks working, we use local state here, and push changes up to Redux
 * in response to those changes via an effect hook.
 *
 * The local state value should not be inspected or used. It is for temporary storage only, until the value
 * can be pushed to Redux.
 *
 * [SUMMARY]
 *
 *    legacyDraftFlowchart (redux)         -> √ The source of truth
 *    setLegacyDraftFlowchart (redux)      -> X Should not be called directly, other than in response to changes to localDraftFlowchart
 *    localLegacyDraftFlowchartPrivate (local)    -> X Should not be inspected or used, other than to send to Redux
 *    setLocalLegacyDraftFlowchartPrivate (local) -> √ Use this on changes to the flowchart
 *    legacyDraftFlowchartRef              -> √ Use during saves, or other async operations that require perfect data
 *
 */
export function PresentationalProcedurePage({
  procedureMetadata,
  setProcedureMetadata,
  processFlowchartConfigId,
  legacyPersistedFlowchart,
  legacyDraftFlowchart,
  legacyDraftFlowchartRef,
  setLegacyDraftFlowchart: _setLegacyDraftFlowchart,
  legacyDirtyState,
  readonlyState,
  legacyIsSaveInFlightState,
  legacyIsAutosaverPausedState,
  legacyIsAutosaverBlockedState,
  updateAndPersistLegacyProcedure,
  duplicateProcedure,
  legacyFullyBlockedOnServerError,
  currentFlowchartVersionMetadata,
  lastSavedFlowchartVersionId,
  publishingRecords,
  hardRefresh,
  onSwitchVersionId,
}: PresentationalProcedurePageProps): JSX.Element {
  const isProcedurePoW =
    processFlowchartConfigId != null && BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION;
  const {id: procedureId} = procedureMetadata;
  const [_localLegacyDraftFlowchartPrivate, _setLocalLegacyDraftFlowchartPrivate] =
    useStateIfMounted<ProcedureFlowchart>(legacyPersistedFlowchart);
  const [legacyMaterialFlows, setLegacyMaterialFlows] = useStateIfMounted<Array<MaterialFlow>>(
    getMaterialFlows(legacyPersistedFlowchart)
  );
  const [legacyIsProcedureDirty, legacySetProcedureDirty] = legacyDirtyState;
  const [legacyIsEditingMethodTab, setLegacyIsEditingMethodTab] = useState<boolean>(false);
  const [legacyIsSaving] = legacyIsSaveInFlightState;
  const [legacySelectedMethodCid, setLegacySelectedMethodCid] = useState<string | null>(null);
  const [accessLevel, setAccessLevel] = useStateIfMounted<null | ProcedureAccessLevel>(null);
  const [anyTabValidationErrors, setAnyTabValidationErrors] = useState(false);
  // Increment this value to force the ProcedureEditor component to be rebuilt by flushing its internal state
  // when staged and unsaved changes are reset via resetDraftFlowchartChanges
  const [procedureEditorKey, setProcedureEditorKey] = useStateIfMounted(0);
  // Downstream components can patch into calls to `saveImmediately`
  const {addLegacyMiddleware, removeLegacyMiddleware, legacyMiddleware} =
    useLegacyPreSaveImmediatelyMiddleware();
  const editingMethodNameState = useEditingMethodNameState(
    legacyDraftFlowchart,
    procedureMetadata.procedureType
  );
  // The versionID is only needed in PoW. In legacy procedures, this component already gets the fully loaded procedure object.
  // But in PoW, the version ID from the history manager is handed to the PoW renderer, which loads the version as needed.
  const {versionIdToView} = HistoryManagerContext.useContext();

  // Changes to the local draft flowchart are reported to the state manager, who pushes it into redux
  useEffect(() => {
    _setLegacyDraftFlowchart(_localLegacyDraftFlowchartPrivate);
  }, [_localLegacyDraftFlowchartPrivate, _setLegacyDraftFlowchart]);

  const setLegacyLocalDraftFlowchart = useCallback(
    // Use state updater callback so long running async threads can set draft flowchart state based on the
    // current state instead of stale state from when the async thread started.
    (fcUpdater: (fc: ProcedureFlowchart) => ProcedureFlowchart): void => {
      _setLocalLegacyDraftFlowchartPrivate((pendingFc) => {
        const updatedFc = fcUpdater(pendingFc);
        if (
          !isEqual(
            normalizeFlowchartOrderingForComparison(updatedFc),
            normalizeFlowchartOrderingForComparison(legacyPersistedFlowchart)
          )
        ) {
          legacySetProcedureDirty(true);
        }
        setLegacyMaterialFlows(getMaterialFlows(updatedFc));
        return updatedFc;
      });
    },
    [
      _setLocalLegacyDraftFlowchartPrivate,
      legacyPersistedFlowchart,
      setLegacyMaterialFlows,
      legacySetProcedureDirty,
    ]
  );

  // Create stable refs for saveImmediately to avoid unnecessary re-creation. Without these, the
  // `saveImmediately` function would change whenever we update the callback function `setLegacyLocalDraftFlowchart`
  // which does not use `useState`, so it will change whenever the `legacyPersistedFlowchart` changes.
  // If we don't do the jump through the `ref`, then we end up recreating the `saveImmediately` function which
  // then causes unnecessarily effects downstream.
  const setLegacyLocalDraftFlowchartRef = useRef(setLegacyLocalDraftFlowchart);
  const legacyMiddlewareRef = useRef(legacyMiddleware);

  useEffect(() => {
    setLegacyLocalDraftFlowchartRef.current = setLegacyLocalDraftFlowchart;
    legacyMiddlewareRef.current = legacyMiddleware;
  }, [setLegacyLocalDraftFlowchart, legacyMiddleware]);

  const resetLegacyDraftFlowchartChanges = useCallback((): void => {
    legacySetProcedureDirty(false);
    // Trigger re-render of ProcedureEditor and flush its internal state
    setProcedureEditorKey((c) => c + 1);
    setLegacyLocalDraftFlowchart(() => legacyPersistedFlowchart);
  }, [
    legacySetProcedureDirty,
    setProcedureEditorKey,
    setLegacyLocalDraftFlowchart,
    legacyPersistedFlowchart,
  ]);

  // Utility to defer running setDraftFlowchart till pending updates to persistedFlowchart are visible.
  // This is only needed when the code causes a change in persistedFlowchart (eg. refetch) and tries to call
  // setDraftFlowchart within the same synchronous code path. That code would see a stale value for
  // persistedFlowchart.
  const [pendingDraftFlowchartUpdate, setPendingDraftFlowchartUpdate] = useStateIfMounted<{
    updater: (fc: ProcedureFlowchart) => ProcedureFlowchart;
  } | null>(null);

  useEffect(() => {
    if (pendingDraftFlowchartUpdate != null) {
      setLegacyLocalDraftFlowchart(pendingDraftFlowchartUpdate.updater);
      setPendingDraftFlowchartUpdate(null);
    }
  }, [pendingDraftFlowchartUpdate, setLegacyLocalDraftFlowchart, setPendingDraftFlowchartUpdate]);

  useAllTagSchemasWithStandardShape((schemas) => {
    setAccessLevel(determineProcedureAccessLevel(legacyDraftFlowchart, schemas));
  });

  useEffect(() => {
    setWindowTitle(procedureMetadata.name);
  }, [procedureMetadata.name]);

  const {readOnlyBannerMessage, isProcedureReadOnly, isProcedureArchived} = useProcedureReadonlyState({
    procedureMetadata,
    accessLevel,
    legacyFullyBlockedOnServerError,
    currentFlowchartVersionMetadata,
    publishingRecords,
    readonlyState,
    legacyIsAutosaverBlockedState,
    legacyIsSaveInFlightState,
  });

  // Only run legacy validation for legacy procedures. This can be removed once we remove the legacy procedure
  // code.
  const legacyProcedureValidationErrors = useValidateProcedure(
    isProcedurePoW ? null : legacyDraftFlowchart,
    procedureMetadata.procedureType,
    procedureId
  );
  // Memoized here to prevent unnecessary creation of the empty array when the procedure is PoW. This kept
  // causing a infinite re-render loop.
  const procedureValidationErrors = useMemo<Array<ProcedureValidationError>>(
    // PoW procedures are validated by ProcedureFlowchartValidator.
    () => (isProcedurePoW ? [] : legacyProcedureValidationErrors),
    [isProcedurePoW, legacyProcedureValidationErrors]
  );

  /**
   * "Blocked" is a fundamental situation. E.g., server error. This should not be controlled downstream.
   * "Paused" is a UX concern. E.g., during a modal editing operation that requires quiet.
   */
  const [legacyShouldPauseAutosave, setLegacyShouldPauseAutosave] = legacyIsAutosaverPausedState;

  const legacySaveImmediately = useLegacyProcedureSaveTools({
    isProcedurePoW,
    procedureId,
    processFlowchartConfigId,
    legacyDraftFlowchartRef,
    legacyMiddlewareRef,
    setLegacyLocalDraftFlowchartRef,
    setProcedureMetadata,
    updateAndPersistLegacyProcedure,
    setPendingDraftFlowchartUpdate,
    lastSavedFlowchartVersionId,
    saveProcedureWhenUnmountedFactory: saveLegacyProcedureWhenUnmountedFactory,
  });

  const {handleRename} = useProcedureActions({
    isProcedurePoW,
    procedureMetadata,
    setProcedureMetadata,
    legacySaveImmediately,
    procedureId,
    hardRefresh,
  });

  // These tools were used in the legacy procedures header
  const {
    handleLegacyModifyFlowchart,
    handleLegacyStopEditingMethod,
    handleLegacyStartEditingMethod,
    handleLegacyAddMethod,
  } = useLegacyProcedureActions({
    isProcedurePoW,
    procedureMetadata,
    procedureId,
    setLegacyLocalDraftFlowchart,
    legacyDraftFlowchartRef,
    legacySelectedMethodCid,
    setLegacySelectedMethodCid,
    autofocusEditNameOnNewMethod: editingMethodNameState.autofocusEditNameOnNewMethod,
  });

  useEffect(() => {
    // Displays confirmation on close tab and reload event
    const handleDisplayCloseTabAndReload = (e: BeforeUnloadEvent): void => {
      if (legacyIsProcedureDirty) {
        e.preventDefault();
        e.returnValue = ''; // needed to make default pop up show when closing tab
      }
    };

    window.addEventListener('beforeunload', handleDisplayCloseTabAndReload);
    return () => {
      clearNavigationWarnings();
      window.removeEventListener('beforeunload', handleDisplayCloseTabAndReload);
    };
  }, [legacyIsProcedureDirty]);

  const [legacyCurrentTab, _setLegacyCurrentTab] = useState(DEFAULT_METHOD_EDITOR_TAB);
  const legacyMethodCidToMethodNameMap = objectFromEntries(
    legacyDraftFlowchart.nodes.map((node) => [node.cid, node.name])
  );

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const procedurePageContextValue = {
    legacyIsProcedureDirty,
    legacyIsSaving,
    isProcedurePoW,
    legacySaveImmediately,
    registerLegacyPreSaveImmediatelyMiddleware: addLegacyMiddleware,
    unregisterLegacyPreSaveImmediatelyMiddleware: removeLegacyMiddleware,
    legacySelectedMethodCid,
    setLegacySelectedMethodCid,
    legacyIsEditingMethodTab,
    setLegacyIsEditingMethodTab,
    legacyShouldPauseAutosave,
    setLegacyShouldPauseAutosave,
    legacyMethodCidToMethodNameMap,
    legacyMaterialFlows,
    resetLegacyDraftFlowchartChanges,
    legacyCurrentTab,
    legacyDangerouslySetCurrentTab: _setLegacyCurrentTab,
    accessLevel,
    procedureMetadata,
    setProcedureMetadata,
    currentFlowchartVersionMetadata,
    hardRefresh,
    duplicateProcedure,
  };
  const validationContextValue = useMemo(
    () => ({
      procedureValidationErrors,
      anyTabValidationErrors,
      setAnyTabValidationErrors,
    }),
    [procedureValidationErrors, anyTabValidationErrors, setAnyTabValidationErrors]
  );

  const structuredTableInsertionContextValue = useMemo(
    () => ({itemInsertedFromId: procedureId}),
    [procedureId]
  );

  return (
    <div
      {...getTestProps({component: 'ProcedurePage', element: 'root'})}
      style={{display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'hidden'}}
    >
      <ProcedurePageContext.Provider value={procedurePageContextValue}>
        <ProcedureValidationContext.Provider value={validationContextValue}>
          <StructuredTableInsertionContext.Provider value={structuredTableInsertionContextValue}>
            {BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION && processFlowchartConfigId != null ? (
              <ProcessFlowchartConfigEditor
                flowchartTypeName={ProcedureProcessesPlugin.FLOWCHART_TYPE_NAME}
                flowchartConfigId={processFlowchartConfigId}
                flowchartConfigVersionId={versionIdToView}
                locked={isProcedureReadOnly}
                lockErrorMessage={readOnlyBannerMessage ?? undefined}
                headerConfigAdditionalProps={{
                  flowchartNameOverride: procedureMetadata.name,
                  dynamicBreadcrumbs: [
                    {
                      label: procedureMetadata.templateCollection.name,
                      href: procedureMetadata.templateCollection.url,
                    },
                  ],
                  dynamicIcon: getProcedureIcon(procedureMetadata.procedureType),
                  onRename: handleRename,
                }}
              />
            ) : (
              <>
                {/* For PoW procedures, we rely on the ProcessFlowchartConfigEditorHeader provided by Workflows */}
                <LegacyProcedureEditorHeader
                  methodNodes={legacyDraftFlowchart.nodes}
                  onRename={handleRename}
                  onAddMethod={() => {
                    handleLegacyAddMethod({shouldSwitchViewToFlowchart: true, shouldOpenNameEditor: true});
                  }}
                  onSwitchVersionId={onSwitchVersionId}
                  isProcedureReadOnly={isProcedureReadOnly}
                  key={procedureId}
                />
                {readOnlyBannerMessage && <BufferOptions banner={readOnlyBannerMessage} fullScreen={false} />}
                <LegacyProcedureEditor
                  flowchart={legacyDraftFlowchartRef.current}
                  modifyFlowchart={handleLegacyModifyFlowchart}
                  procedureId={procedureMetadata.id}
                  templateCollectionId={procedureMetadata.templateCollection.id}
                  isProcedureReadOnly={isProcedureReadOnly}
                  isProcedureArchived={isProcedureArchived}
                  procedureEditorKey={procedureEditorKey}
                  onStopEditingMethod={handleLegacyStopEditingMethod}
                  onStartEditingMethod={handleLegacyStartEditingMethod}
                  editingMethodNameState={editingMethodNameState}
                />
              </>
            )}
            <ProcedureContextRetriever pageContextValue={procedurePageContextValue} />
          </StructuredTableInsertionContext.Provider>
        </ProcedureValidationContext.Provider>
      </ProcedurePageContext.Provider>
    </div>
  );
}

// Used for testing only
function ProcedureContextRetriever(props: {pageContextValue: ProcedurePageContextValue}): null {
  return null;
}

const proceduresPlugin = new ProcedureProcessesPlugin();
proceduresPlugin.registerForEditor();
